import React, {useState, useEffect} from 'react'
import {Modal, Button, Form, InputGroup} from 'react-bootstrap'
import {useLazyQuery} from '@apollo/client'
import {GET_ALL_RECIPIENTS} from '../../../gql/queries/contactsApiQueries'
import {IResRecipientsAPI, NodeContact} from '../../../types/contactsApi'
import {formatPhoneDisplay} from '../../../../../utils/phoneUtils'
import {CSVImportModal} from './CSVImportModal'
import './RecipientSelectorModal.css'

interface RecipientSelectorModalProps {
  show: boolean
  onHide: () => void
  initialSelectedIds: string[]
  onSave: (selectedContactIds: string[]) => void
}

export const RecipientSelectorModal: React.FC<RecipientSelectorModalProps> = ({
  show,
  onHide,
  initialSelectedIds,
  onSave,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds)
  const [highlightedAvailable, setHighlightedAvailable] = useState<string[]>([])
  const [highlightedSelected, setHighlightedSelected] = useState<string[]>([])
  const [showCSVImport, setShowCSVImport] = useState(false)

  const [getRecipients, {data, loading, error}] = useLazyQuery<IResRecipientsAPI>(GET_ALL_RECIPIENTS, {
    fetchPolicy: 'network-only',
  })

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch contacts when search changes
  useEffect(() => {
    if (show) {
      getRecipients({
        variables: {
          name: debouncedSearch || undefined,
          first: 100,
          offset: 0,
          subscriptionStatus: undefined,
        },
      })
    }
  }, [debouncedSearch, show, getRecipients])

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setSelectedIds(initialSelectedIds)
      setHighlightedAvailable([])
      setHighlightedSelected([])
      setSearchTerm('')
      setShowCSVImport(false)
    }
  }, [show, initialSelectedIds])

  const allRecipients = data?.allRecipients?.edges || []
  const availableContacts = allRecipients.filter((edge) => !selectedIds.includes(edge.node.id))
  const selectedContacts = allRecipients.filter((edge) => selectedIds.includes(edge.node.id))

  // Move handlers
  const moveToSelected = () => {
    setSelectedIds([...selectedIds, ...highlightedAvailable])
    setHighlightedAvailable([])
  }

  const moveToAvailable = () => {
    setSelectedIds(selectedIds.filter((id) => !highlightedSelected.includes(id)))
    setHighlightedSelected([])
  }

  const moveAllToSelected = () => {
    const allAvailableIds = availableContacts.map((edge) => edge.node.id)
    setSelectedIds([...selectedIds, ...allAvailableIds])
  }

  const moveAllToAvailable = () => {
    setSelectedIds([])
    setHighlightedSelected([])
  }

  // Highlight toggle handlers
  const toggleAvailableHighlight = (id: string) => {
    setHighlightedAvailable((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const toggleSelectedHighlight = (id: string) => {
    setHighlightedSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const handleSave = () => {
    onSave(selectedIds)
  }

  const handleImportSuccess = (importedContactIds: string[]) => {
    // Add imported contacts to selected IDs (avoid duplicates)
    const combined = [...selectedIds, ...importedContactIds]
    const unique = combined.filter((id, index) => combined.indexOf(id) === index)
    setSelectedIds(unique)
    // Refresh contacts list
    getRecipients({
      variables: {
        name: debouncedSearch || undefined,
        first: 100,
        offset: 0,
        subscriptionStatus: undefined,
      },
    })
  }

  const renderContactItem = (
    contact: NodeContact,
    isHighlighted: boolean,
    onToggle: () => void
  ) => (
    <div
      key={contact.id}
      className={`recipient-item ${isHighlighted ? 'highlighted' : ''}`}
      onClick={onToggle}
    >
      <div className='form-check'>
        <input
          className='form-check-input'
          type='checkbox'
          checked={isHighlighted}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
        />
        <div className='recipient-info'>
          <div className='fw-bold'>{contact.fullName || `${contact.firstName} ${contact.lastName}`}</div>
          <div className='text-muted small'>
            {contact.email && <span className='me-3'>{contact.email}</span>}
            {contact.phone && <span>{formatPhoneDisplay(contact.phone)}</span>}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <Modal show={show} onHide={onHide} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title>Select Campaign Recipients</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='recipient-selector-container'>
          {/* Available Column */}
          <div className='recipient-column'>
            <div className='column-header'>
              <h6 className='mb-3'>Available Contacts ({availableContacts.length})</h6>
              <div className='d-flex gap-2 mb-3'>
                <InputGroup style={{flex: 1}}>
                  <Form.Control
                    type='text'
                    placeholder='🔍 Search contacts...'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
                <Button
                  variant='outline-success'
                  size='sm'
                  onClick={() => setShowCSVImport(true)}
                  title='Import contacts from CSV'
                >
                  <i className='bi bi-file-earmark-arrow-up me-1'></i>
                  Import CSV
                </Button>
              </div>
            </div>
            <div className='contacts-list'>
              {error ? (
                <div className='text-center text-danger p-4 small'>
                  <i className='bi bi-exclamation-triangle me-2'></i>
                  {error.message}
                </div>
              ) : loading ? (
                <div className='text-center p-4'>
                  <div className='spinner-border spinner-border-sm' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                </div>
              ) : availableContacts.length === 0 ? (
                <div className='text-center text-muted p-4'>
                  <i className='bi bi-inbox fs-1 d-block mb-2'></i>
                  No contacts available
                </div>
              ) : (
                availableContacts.map(({node: contact}) =>
                  renderContactItem(
                    contact,
                    highlightedAvailable.includes(contact.id),
                    () => toggleAvailableHighlight(contact.id)
                  )
                )
              )}
            </div>
          </div>

          {/* Control Buttons */}
          <div className='control-buttons'>
            <Button
              variant='outline-primary'
              size='sm'
              onClick={moveToSelected}
              disabled={highlightedAvailable.length === 0}
              title='Move selected to recipients'
            >
              <i className='bi bi-chevron-right'></i>
            </Button>
            <Button
              variant='outline-primary'
              size='sm'
              onClick={moveAllToSelected}
              disabled={availableContacts.length === 0}
              title='Move all to recipients'
            >
              <i className='bi bi-chevron-double-right'></i>
            </Button>
            <Button
              variant='outline-secondary'
              size='sm'
              onClick={moveToAvailable}
              disabled={highlightedSelected.length === 0}
              title='Remove selected from recipients'
            >
              <i className='bi bi-chevron-left'></i>
            </Button>
            <Button
              variant='outline-secondary'
              size='sm'
              onClick={moveAllToAvailable}
              disabled={selectedIds.length === 0}
              title='Remove all from recipients'
            >
              <i className='bi bi-chevron-double-left'></i>
            </Button>
          </div>

          {/* Selected Column */}
          <div className='recipient-column'>
            <div className='column-header'>
              <h6 className='mb-3'>
                Selected Recipients ({selectedIds.length})
              </h6>
              <div className='mb-3' style={{height: '38px'}}>
                {selectedIds.length > 0 && (
                  <div className='alert alert-success mb-0 py-1 small'>
                    <i className='bi bi-check-circle me-2'></i>
                    {selectedIds.length} contact{selectedIds.length !== 1 && 's'} ready
                  </div>
                )}
              </div>
            </div>
            <div className='contacts-list'>
              {selectedContacts.length === 0 ? (
                <div className='text-center text-muted p-4'>
                  <i className='bi bi-person-plus fs-1 d-block mb-2'></i>
                  No recipients selected yet
                  <br />
                  <small>Select contacts from the left</small>
                </div>
              ) : (
                selectedContacts.map(({node: contact}) =>
                  renderContactItem(
                    contact,
                    highlightedSelected.includes(contact.id),
                    () => toggleSelectedHighlight(contact.id)
                  )
                )
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleSave} disabled={selectedIds.length === 0}>
          Save Selection ({selectedIds.length})
        </Button>
      </Modal.Footer>

      {/* CSV Import Modal */}
      <CSVImportModal
        show={showCSVImport}
        onHide={() => setShowCSVImport(false)}
        onImportSuccess={handleImportSuccess}
      />
    </Modal>
  )
}
